<template>
	<v-card>
		<v-card-title class="d-flex align-center">
			<v-icon
				icon="$pages"
				start
			/>
			<translate>Documents</translate>
		</v-card-title>
		<v-card-text>
			<v-row
				v-if="documentType === 'claim'"
			>
				<v-col class="text-right">
					<v-btn
						@click="downloadPolicySheet()"
					>
						<img src="/bo/images/icons/page_white_acrobat.png">
						<translate>Policy Sheet</translate>
					</v-btn>
				</v-col>
			</v-row>
			<div
				v-if="readPermission"
			>
				<v-row>
					<v-col>
						<v-data-table
							:headers="headers"
							:items="files"
							hide-default-footer
							disable-pagination
							disable-sort
						>
							<template #item.file="{ item }">
								<a @click="downloadFile(item.id)">
									{{ item.file }}
								</a>
							</template>
							<template #item.visibility="{ item }">
								<v-checkbox-btn
									v-model="item.visibility"
									color="primary"
									:disabled="!createPermission"
								/>
							</template>
							<template #item.createdAt.date="{ item }">
								{{ item.createdAt.date.split(' ')[0] }}
							</template>
							<template #item.delete="{ item }">
								<v-btn
									v-if="deletePermission"
									variant="text"
									color="error"
									@click="deleteFile(item.id)"
								>
									<translate>Delete</translate>
								</v-btn>
							</template>
						</v-data-table>
					</v-col>
				</v-row>
				<v-row>
					<v-col
						v-if="createPermission"
					>
						<v-btn
							:loading="changingVisibilities"
							:disabled="changingVisibilities"
							@click="submitVisibilities()"
						>
							<translate>Update Visibilities</translate>
						</v-btn>
					</v-col>
				</v-row>
				<v-row
					v-if="createPermission"
					align="center"
				>
					<v-col cols="4">
						<v-file-input
							v-model="newFile"
							hide-details
							:label="$gettext(`Upload a file`)"
							:error="fileError"
							variant="outlined"
							@change="fileError = false"
						/>
					</v-col>
					<v-col cols="auto">
						<v-checkbox
							v-model="visible"
							:label="$gettext(`Visible to Client?`)"
						/>
					</v-col>
					<v-col>
						<v-btn
							color="primary"
							:loading="uploading"
							:disabled="uploading"
							@click="submit()"
						>
							<translate>Upload </translate>
							<v-icon
								dark
								icon="$cloudUpload"
							/>
						</v-btn>
					</v-col>
				</v-row>
			</div>
			<div v-else>
				<translate>You do not have access to any uploaded documents.</translate>
			</div>
		</v-card-text>
	</v-card>
</template>

<script>

import {
	axios,
} from '../../services/axios-service.js';

export default {
	name: 'DocumentUpload',
	props: {
		id: {
			type: String,
			required: true,
		},

		documentType: {
			type: String,
			required: true,
		},

		createPermission: {
			type: String,
			required: true,
		},

		readPermission: {
			type: String,
			required: true,
		},

		deletePermission: {
			type: String,
			required: true,
		},

		orderrowId: {
			type: String,
			required: false,
			default: ''
		}
	},

	data () {
		return {
			newFile: [],
			files: [],
			visible: false,
			uploading: false,
			fileError: false,
			changingVisibilities: false,
		};
	},

	computed: {
		headers: function () {
			return [
				{
					title: this.$gettext('File Name'), value: 'file',
				},
				{
					title: this.$gettext('Created'), value: 'createdAt.date',
				},
				{
					title: this.$gettext('Document visible for client'), value: 'visibility'
				},
				{
					title: '', value: 'delete'
				},
			];
		},
	},

	mounted () {
		this.retrieveDocuments();
	},

	methods: {

		/**
		 * Retrieves the list of documents to be displayed
		 */
		retrieveDocuments () {
			axios
				.get(`/api/v0/${this.documentType}documents?${this.documentType}_id=${this.id}`)
				.then(({
					data: files,
				}) => {
					files.forEach((file) => {
						file.visibility = Boolean(file.visibility);
					});
					this.files = files;
				})
				.catch((error) => {
					window.captureException(error);
				});
		},

		/**
		 * @param {string} id
		 * @returns {void}
		 */
		downloadFile (id) {
			window.location.href = `/api/v0/${this.documentType}documents/download?id=${id}`;
		},

		/**
		 * Upload a new file
		 *
		 * @returns {void}
		 */
		submit () {
			this.uploading = true;

			if (this.newFile.length === 0) {
				this.fileError = true;
				this.uploading = false;
			} else {
				const formData = new FormData();

				formData.append(`${this.documentType}_id`, this.id);
				formData.append('document', this.newFile[0]);
				formData.append('visibility', this.visible);
				axios
					.post(`/api/v0/${this.documentType}documents`, formData, {
						headers: {
							'Content-Type': 'multipart/form-data',
						},
					})
					.then(() => {
						this.newFile = [];
						this.retrieveDocuments();
					})
					.catch((error) => {
						window.captureException(error);
					})
					.finally(() => {
						this.uploading = false;
						this.visible = false;
					});
			}
		},

		/**
		 * Add the visibilities to the formData
		 *
		 * @param {FormData} formData
		 * @returns {FormData}
		 */
		addVisibilities (formData) {
			for (const [, file, ] of this.files.entries()) {
				formData.append(`visibilities[${file.id}]`, Number(file.visibility));
			}
			return formData;
		},

		/**
		 * Change visibilities of the files
		 *
		 * @returns {void}
		 */
		submitVisibilities () {
			this.changingVisibilities = true;

			let formData = new FormData();

			formData.append(`${this.documentType}_id`, this.id);
			formData = this.addVisibilities(formData);

			axios
				.post(`/api/v0/${this.documentType}documents/visibilities`, formData)
				.catch((error) => {
					window.captureException(error);
				})
				.finally(() => {
					this.changingVisibilities = false;
				});
		},

		/**
		 * Delete the specified file
		 *
		 * @param {string} id
		 * @returns {void}
		 */
		deleteFile (id) {
			axios
				.delete(`/api/v0/${this.documentType}documents?id=${id}`)
				.catch((error) => {
					window.captureException(error);
				})
				.finally(() => {
					this.retrieveDocuments();
				});
		},

		/**
		 * Download the policy sheet for a claim
		 *
		 * @returns {void}
		 */
		downloadPolicySheet () {
			window.open(`/bo/orders/policysheet?orderrow_id=${this.orderrowId}`);
		}
	}
};
</script>
